import { useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { Booking, BookingInput, Machine, Site, Subcontractor, SubcontractorUser } from '../types';
import AppDialog from '../../../components/AppDialog';
import { BOOKING_MODAL_LABELS as LABELS } from '../../../utils/labels';
import { useModalHistory } from '../../../utils/useModalHistory';
import SubcontractorModal from '../../settings/components/subcontractors/SubcontractorModal';
import SiteModal from '../../settings/components/sites/SiteModal';
import BookingContactUserSelect from './BookingContactUserSelect';
import type { SettingsSiteRow } from '../../settings/types';

const CREATE_SENTINEL = '__create__';

interface BookingForm {
  machine_id: string;
  subcontractor_id: string;
  subcontractor_user_id: string;
  site_id: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  notes: string;
}

type FormErrors = Partial<Record<keyof BookingForm, string>>;

interface BookingModalProps {
  machines: Machine[];
  sites: Site[];
  subcontractors: Subcontractor[];
  booking: Booking | undefined;
  lockedMachineId: string | null;
  prefilledStartDate: string | null;
  defaultStartTime?: string;
  defaultEndTime?: string;
  activeProjects: { id: string; name: string; year: number }[];
  canCreateSite: boolean;
  onClose: () => void;
  onSave: (machineId: string, data: BookingInput) => void;
  onAddSubcontractor: (name: string) => Promise<Subcontractor | null>;
  onAddSubcontractorUser: (
    subcontractorId: string,
    firstName: string,
    lastName: string,
  ) => Promise<SubcontractorUser | null>;
  onAddSite: (name: string, projectId: string | null, color: string) => Promise<Site | null>;
}

export default function BookingModal({
  machines,
  sites,
  subcontractors,
  lockedMachineId,
  prefilledStartDate,
  booking,
  defaultStartTime,
  defaultEndTime,
  activeProjects,
  canCreateSite,
  onClose,
  onSave,
  onAddSubcontractor,
  onAddSubcontractorUser,
  onAddSite,
}: BookingModalProps) {
  useModalHistory(onClose);

  const [quickCreate, setQuickCreate] = useState<'subcontractor' | 'site' | null>(null);

  const allSubcontractors = subcontractors;
  const allSites = sites;

  const [form, setForm] = useState<BookingForm>({
    machine_id: lockedMachineId ?? booking?.machine_id ?? machines[0]?.id ?? '',
    subcontractor_id: booking?.subcontractor_id ?? '',
    subcontractor_user_id: booking?.subcontractor_user_id ?? '',
    site_id: booking?.site_id ?? '',
    start_date: booking?.start_date ?? prefilledStartDate ?? '',
    start_time: booking?.start_time ?? defaultStartTime ?? '08:00',
    end_date: booking?.end_date ?? '',
    end_time: booking?.end_time ?? defaultEndTime ?? '17:00',
    notes: booking?.notes ?? '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const set = <K extends keyof BookingForm>(k: K, v: BookingForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const handleStartDateChange = (val: ReturnType<typeof dayjs> | null) => {
    const newDate = val ? val.format('YYYY-MM-DD') : '';
    setForm((f) => {
      const next = { ...f, start_date: newDate };
      if (newDate && next.end_date === newDate && next.end_time && next.end_time <= f.start_time) {
        next.end_time = '';
      }
      return next;
    });
    setErrors((e) => ({ ...e, start_date: undefined, end_date: undefined, end_time: undefined }));
  };

  const handleStartTimeChange = (val: ReturnType<typeof dayjs> | null) => {
    set('start_time', val ? val.format('HH:mm') : '');
  };

  const handleEndDateChange = (val: ReturnType<typeof dayjs> | null) => {
    const newDate = val ? val.format('YYYY-MM-DD') : '';
    setForm((f) => {
      const next = { ...f, end_date: newDate };
      if (newDate && next.start_date === newDate && next.start_time && f.end_time && next.start_time >= f.end_time) {
        next.start_time = '';
      }
      return next;
    });
    setErrors((e) => ({ ...e, end_date: undefined, start_date: undefined, start_time: undefined }));
  };

  const handleEndTimeChange = (val: ReturnType<typeof dayjs> | null) => {
    set('end_time', val ? val.format('HH:mm') : '');
  };

  const selectedMachine = machines.find((m) => m.id === (lockedMachineId ?? form.machine_id));

  const toMinutes = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };

  const toDateTimeKey = (date: string, time: string) => `${date}T${time.slice(0, 5)}`;

  const overlapsWith = (other: Booking) => {
    const aStart = toDateTimeKey(form.start_date, form.start_time);
    const aEnd = toDateTimeKey(form.end_date, form.end_time);
    const bStart = toDateTimeKey(other.start_date, other.start_time);
    const bEnd = toDateTimeKey(other.end_date, other.end_time);
    return aStart < bEnd && bStart < aEnd;
  };

  const overlappingBooking =
    form.start_date && form.start_time && form.end_date && form.end_time
      ? selectedMachine?.bookings?.find((b) => b.id !== booking?.id && overlapsWith(b))
      : undefined;

  const isSameDay = form.start_date && form.start_date === form.end_date;

  const startDateMin = selectedMachine?.start_date ? dayjs(selectedMachine.start_date) : undefined;
  const startDateMax = (() => {
    const a = form.end_date || undefined;
    const b = selectedMachine?.end_date;
    if (!a && !b) return undefined;
    if (!a) return dayjs(b);
    if (!b) return dayjs(a);
    return dayjs(a < b ? a : b);
  })();
  const endDateMin = (() => {
    const a = form.start_date || undefined;
    const b = selectedMachine?.start_date;
    if (!a && !b) return undefined;
    if (!a) return dayjs(b);
    if (!b) return dayjs(a);
    return dayjs(a > b ? a : b);
  })();
  const endDateMax = selectedMachine?.end_date ? dayjs(selectedMachine.end_date) : undefined;

  const shouldDisableEndTime = (value: ReturnType<typeof dayjs>, view: 'hours' | 'minutes') => {
    const h = value.hour();
    const m = view === 'hours' ? 0 : value.minute();

    // disable if <= booking start_time (same day as booking start)
    if (isSameDay && form.start_time) {
      const start = toMinutes(form.start_time);
      if (view === 'hours' ? h * 60 + 30 <= start : h * 60 + m <= start) return true;
    }

    // disable if > machine end_time (on machine's last day)
    if (selectedMachine?.end_date && form.end_date === selectedMachine.end_date && selectedMachine.end_time) {
      const machineEnd = toMinutes(selectedMachine.end_time);
      if (view === 'hours' ? h * 60 > machineEnd : h * 60 + m > machineEnd) return true;
    }

    return false;
  };

  const shouldDisableStartTime = (value: ReturnType<typeof dayjs>, view: 'hours' | 'minutes') => {
    const h = value.hour();
    const m = view === 'hours' ? 0 : value.minute();

    // disable if >= booking end_time (same day as booking end)
    if (isSameDay && form.end_time) {
      const end = toMinutes(form.end_time);
      if (view === 'hours' ? h * 60 >= end : h * 60 + m >= end) return true;
    }

    // disable if < machine start_time (on machine's first day)
    if (selectedMachine?.start_date && form.start_date === selectedMachine.start_date && selectedMachine.start_time) {
      const machineStart = toMinutes(selectedMachine.start_time);
      if (view === 'hours' ? h * 60 + 30 < machineStart : h * 60 + m < machineStart) return true;
    }

    return false;
  };

  const handleSubcontractorChange = (subId: string) => {
    const sub = allSubcontractors.find((s) => s.id === subId);
    const defaultUser = sub?.subcontractor_users?.find((u) => u.is_default);
    setForm((f) => ({ ...f, subcontractor_id: subId, subcontractor_user_id: defaultUser?.id ?? '' }));
    setErrors((e) => ({ ...e, subcontractor_id: undefined, subcontractor_user_id: undefined }));
  };

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!lockedMachineId && !form.machine_id) e.machine_id = LABELS.machineRequired;
    if (!form.subcontractor_id) e.subcontractor_id = LABELS.subcontractorRequired;
    if (!form.subcontractor_user_id) e.subcontractor_user_id = LABELS.userRequired;
    if (!form.site_id) e.site_id = LABELS.siteRequired;
    if (!form.start_date) e.start_date = LABELS.startDateRequired;
    if (!form.end_date) e.end_date = LABELS.endDateRequired;
    if (form.start_date && form.end_date && form.end_date < form.start_date)
      e.end_date = LABELS.endDateAfterStart;
    if (form.start_date && form.end_date && form.end_date === form.start_date && form.start_time && form.end_time && form.end_time <= form.start_time)
      e.end_time = LABELS.endTimeAfterStart;
    if (selectedMachine?.start_date && form.start_date && form.start_date < selectedMachine.start_date)
      e.start_date = LABELS.bookingStartBeforeMachine;
    if (!e.start_date && selectedMachine?.start_date && selectedMachine.start_time && form.start_date === selectedMachine.start_date && form.start_time && form.start_time < selectedMachine.start_time.slice(0, 5))
      e.start_time = LABELS.bookingStartTimeBeforeMachine;
    if (selectedMachine?.end_date && form.end_date && form.end_date > selectedMachine.end_date)
      e.end_date = LABELS.bookingEndAfterMachine;
    if (!e.end_date && selectedMachine?.end_date && selectedMachine.end_time && form.end_date === selectedMachine.end_date && form.end_time && form.end_time > selectedMachine.end_time.slice(0, 5))
      e.end_time = LABELS.bookingEndTimeAfterMachine;
    return e;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    onSave(form.machine_id, {
      subcontractor_id: form.subcontractor_id,
      subcontractor_user_id: form.subcontractor_user_id,
      site_id: form.site_id,
      start_date: form.start_date,
      start_time: form.start_time,
      end_date: form.end_date,
      end_time: form.end_time,
      notes: form.notes,
    });
    onClose();
  };

  const selectedSubcontractor = allSubcontractors.find((s) => s.id === form.subcontractor_id);
  const availableUsers = selectedSubcontractor?.subcontractor_users ?? [];

  const activeSitesForQuickCreate: SettingsSiteRow[] = allSites.map((s) => ({
    ...s,
    project_name: null,
    project_year: null,
    has_bookings: false,
  }));

  return (
    <>
    <AppDialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={booking ? LABELS.editTitle : LABELS.addTitle}
      confirmLabel={LABELS.saveButton}
      confirmFormId="booking-form"
      testId="booking-modal"
    >
      <form id="booking-form" onSubmit={submit} noValidate>
        <DialogContent>
          <div className="flex flex-col gap-4">
            {/* Machine */}
            <FormControl fullWidth required error={!!errors.machine_id}>
              <InputLabel>{LABELS.machineLabel}</InputLabel>
              <Select
                label={LABELS.machineLabel}
                value={lockedMachineId ?? form.machine_id}
                onChange={(e) => set('machine_id', e.target.value)}
                disabled={!!lockedMachineId}
                inputProps={{ 'aria-label': LABELS.machineLabel }}
              >
                {machines.map((m) => (
                  <MenuItem key={m.id} value={m.id} data-testid={`option-${m.id}`}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.machine_id && <FormHelperText>{errors.machine_id}</FormHelperText>}
            </FormControl>

            {/* Subcontractor */}
            <FormControl fullWidth required error={!!errors.subcontractor_id}>
              <InputLabel>{LABELS.subcontractorLabel}</InputLabel>
              <Select
                label={LABELS.subcontractorLabel}
                value={form.subcontractor_id}
                onChange={(e) => {
                  if (e.target.value === CREATE_SENTINEL) {
                    setQuickCreate('subcontractor');
                    return;
                  }
                  handleSubcontractorChange(e.target.value);
                }}
                inputProps={{ 'aria-label': LABELS.subcontractorLabel }}
              >
                <MenuItem value={CREATE_SENTINEL} data-testid="option-add-subcontractor" className="text-primary">
                  <AddIcon fontSize="small" className="mr-1" />
                  {LABELS.addSubcontractorOption}
                </MenuItem>
                {allSubcontractors.length === 0 ? (
                  <MenuItem disabled value="">
                    {LABELS.noSubcontractors}
                  </MenuItem>
                ) : (
                  allSubcontractors.map((s) => (
                    <MenuItem key={s.id} value={s.id} data-testid={`option-${s.id}`}>
                      {s.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.subcontractor_id && (
                <FormHelperText>{errors.subcontractor_id}</FormHelperText>
              )}
            </FormControl>

            {/* User */}
            <BookingContactUserSelect
              label={LABELS.userLabel}
              addUserOptionLabel={LABELS.addUserOption}
              noUsersLabel={LABELS.noUsers}
              subcontractorId={form.subcontractor_id}
              users={availableUsers}
              value={form.subcontractor_user_id}
              onChange={(id) => set('subcontractor_user_id', id)}
              onAddUser={onAddSubcontractorUser}
              error={errors.subcontractor_user_id}
            />

            {/* Site */}
            <FormControl fullWidth required error={!!errors.site_id}>
              <InputLabel>{LABELS.siteLabel}</InputLabel>
              <Select
                label={LABELS.siteLabel}
                value={form.site_id}
                onChange={(e) => {
                  if (e.target.value === CREATE_SENTINEL) {
                    setQuickCreate('site');
                    return;
                  }
                  set('site_id', e.target.value);
                }}
                inputProps={{ 'aria-label': LABELS.siteLabel }}
                renderValue={(v) => {
                  const site = allSites.find((s) => s.id === v);
                  return (
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: site?.color }}
                      />
                      {site?.name}
                    </div>
                  );
                }}
              >
                {canCreateSite && (
                  <MenuItem value={CREATE_SENTINEL} data-testid="option-add-site" className="text-primary">
                    <AddIcon fontSize="small" className="mr-1" />
                    {LABELS.addSiteOption}
                  </MenuItem>
                )}
                {allSites.map((s) => (
                  <MenuItem key={s.id} value={s.id} data-testid={`option-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: s.color }}
                      />
                      {s.name}
                    </div>
                  </MenuItem>
                ))}
              </Select>
              {errors.site_id && <FormHelperText>{errors.site_id}</FormHelperText>}
            </FormControl>

            {/* Dates & times */}
            <div className="grid grid-cols-2 gap-3">
              <DesktopDatePicker
                label={LABELS.startDateLabel}
                value={form.start_date ? dayjs(form.start_date) : null}
                onChange={handleStartDateChange}
                minDate={startDateMin}
                maxDate={startDateMax}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.start_date,
                    helperText: errors.start_date,
                  },
                }}
              />
              <TimePicker
                label={LABELS.startTimeLabel}
                ampm={false}
                minutesStep={30}
                timeSteps={{ minutes: 30 }}
                value={form.start_time ? dayjs(`2000-01-01T${form.start_time}`) : null}
                onChange={handleStartTimeChange}
                shouldDisableTime={shouldDisableStartTime}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.start_time,
                    helperText: errors.start_time,
                  },
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <DesktopDatePicker
                label={LABELS.endDateLabel}
                value={form.end_date ? dayjs(form.end_date) : null}
                onChange={handleEndDateChange}
                minDate={endDateMin}
                maxDate={endDateMax}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.end_date,
                    helperText: errors.end_date,
                  },
                }}
              />
              <TimePicker
                label={LABELS.endTimeLabel}
                ampm={false}
                minutesStep={30}
                timeSteps={{ minutes: 30 }}
                value={form.end_time ? dayjs(`2000-01-01T${form.end_time}`) : null}
                onChange={handleEndTimeChange}
                shouldDisableTime={shouldDisableEndTime}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.end_time,
                    helperText: errors.end_time,
                  },
                }}
              />
            </div>

            {overlappingBooking && (
              <Alert severity="warning">
                {LABELS.bookingOverlapWarning(
                  overlappingBooking.subcontractor?.name ?? ''
                )}
              </Alert>
            )}

            {/* Notes */}
            <TextField
              label={LABELS.notesLabel}
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth
            />
          </div>
        </DialogContent>

        <DialogActions className="px-6 pb-4 pt-1 gap-1">
          <Button onClick={onClose} variant="outlined" color="inherit" data-testid="cancel-button">
            {LABELS.cancelButton}
          </Button>
          <Button type="submit" variant="contained" data-testid="save-button">
            {LABELS.saveButton}
          </Button>
        </DialogActions>
      </form>
    </AppDialog>
    {quickCreate === 'subcontractor' && (
      <SubcontractorModal
        onClose={() => setQuickCreate(null)}
        onSave={async (name) => {
          const created = await onAddSubcontractor(name);
          if (!created) return false;
          handleSubcontractorChange(created.id);
          return true;
        }}
      />
    )}
    {quickCreate === 'site' && (
      <SiteModal
        activeSites={activeSitesForQuickCreate}
        activeProjects={activeProjects}
        onClose={() => setQuickCreate(null)}
        onSave={async (name, projectId, color) => {
          const created = await onAddSite(name, projectId, color);
          if (!created) return false;
          set('site_id', created.id);
          return true;
        }}
      />
    )}
    </>
  );
}
